import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getStoreList } from '@/service/dashboard';

const testData = [
  {area_id: 440104, city_id: 4401, name: "越秀店", picture: "", province_id: 44, store_id: 16,url: "http://120.78.3.126/upload/16/0/"},
  {area_id: 440103, city_id: 4401, name: "测试店", picture: "", province_id: 44, store_id: 59,url: "http://120.78.3.126/upload/59/0/"},
  {area_id: 440103, city_id: 4401,name: "总店",picture: "1F-1.jpg,1F-2.jpg,2F-1.jpg,2F-2.jpg,8F-1.jpg,8F-2.jpg",province_id: 44,store_id: 86,url: "http://120.78.3.126/upload/86/0/"},
  {area_id: 440103, city_id: 4401,name: "西门口店",picture: "1F-1.jpg,1F-2.jpg",province_id: 44,store_id: 87,url: "http://120.78.3.126/upload/87/0/"}
]

const defaultState = {
  fetching: false,
  error: null,
  query: {},
  data: []
}

// ==================== 服务类型映射配置 ====================

/**
 * 服务类型映射函数 - 根据serviceType调用不同的API服务
 * 实现灵活的多服务支持，便于扩展新的API接口
 * @param {string} serviceType - 服务类型标识
 * @param {Object} query - 查询参数
 * @returns {Function} 返回对应的API调用函数
 */
const switchPayload = (serviceType, query) => {
  const payload = {
    default: () => getStoreList(query),
    // 预留其他服务类型扩展点
    // featured: () => getFeaturedStores(query),
    // nearby: () => getNearbyStores(query),
    undefined: () => console.error('Redux: fetchStoreList 请求未定义 serviceType 类型')
  };
  return payload[serviceType] || payload.default;
};

// ==================== 字段适配配置 ====================

/**
 * 字段适配配置表 - 处理不同API返回的字段名差异
 * 解决后端接口字段不一致的问题，提供统一的前端数据结构
 */
const keyConfig = [
  { key: 'default', id: 'id', name: 'name', picture: 'picture', url: 'url' },
  // 预留其他API字段映射
  // { key: 'featured', id: 'store_id', name: 'store_name', picture: 'image_url', url: 'link_url' }
];

const defaultKey = { id: 'id', name: 'name', picture: 'picture', url: 'url' };

// ==================== 数据处理函数 ====================

/**
 * 数据格式化函数 - 根据serviceType和dataType处理数据
 * 支持多种数据格式处理，满足不同业务场景需求
 * 支持多种dataType组合，如 'swiper,storeOption'
 * @param {Array} data - 原始数据数组
 * @param {string} serviceType - 服务类型
 * @param {string} dataType - 数据类型，支持逗号分隔的多种类型
 * @param {Object} query - 查询参数对象
 * @returns {Object} 处理后的数据对象，键为数据类型，值为对应的数据数组
 */
const formatData = ({data, serviceType, dataType = 'default', query = {}}) => {
  if (!Array.isArray(data)) return data;

  // 获取当前服务类型的字段映射
  const fieldMap = keyConfig.find(kc => kc.key === serviceType) || defaultKey;

  // 统一处理所有数据类型（单个或多个）
  const dataTypes = dataType.split(',').map(type => type.trim());
  const result = {};

  dataTypes.forEach(type => {
    result[type] = processSingleDataType({
      data,
      fieldMap,
      dataType: type,
      query
    });
  });

  return result;
};

/**
 * 处理单一数据类型的函数
 * @param {Object} params - 参数对象
 * @param {Array} params.data - 原始数据数组
 * @param {Object} params.fieldMap - 字段映射配置
 * @param {string} params.dataType - 单一数据类型
 * @param {Object} params.query - 查询参数对象
 * @returns {Array} 处理后的数据数组
 */
const processSingleDataType = ({ data, fieldMap, dataType, query }) => {
  switch (dataType) {
    // 基础选项：只要name和store_id
    case 'storeOption':
      return data.map(item => ({
        value: item[fieldMap.name],
        id: `${item.store_id}`
      }));

    // 图片列表：根据store_id过滤并重新处理图片
    case 'pictureList': {
      if (!data?.length) return [];
      const targetId = query.store_id || data[0]?.store_id;
      const targetItem = data.find(item => item.store_id === targetId)

      const baseUrl = targetItem[fieldMap.url] || '';
      return targetItem.picture
        .split(',')
        // .map(pic => pic.trim())
        .filter(Boolean)
        .map((pic, index) => ({
          id: `${targetItem.store_id}_${index}`, // 确保有唯一id
          key: `${pic}`,
          store_id: targetItem.store_id,
          picture: pic,
          url: baseUrl.endsWith('T') ? `${baseUrl}${pic}` : `${baseUrl}T${pic}`
        }));
    }

    // 默认处理：过滤无效图片并取第一张
    case 'swiper': {
      return data
      .filter(item => item[fieldMap.picture]?.trim())
      .map((item, index) => {
        const pictures = item[fieldMap.picture]
          .split(',')
          .map(pic => pic.trim())
          .filter(Boolean);

        const baseUrl = item[fieldMap.url] || '';
        return {
          ...item,
          id: item.store_id || item.id || index, // 确保有唯一id
          [fieldMap.url]: pictures[0]
            ? baseUrl.endsWith('T') ? `${baseUrl}${pictures[0]}` : `${baseUrl}T${pictures[0]}`
            : item[fieldMap.url]
        };
      });
    }
    default: []
  }
};
// ==================== 异步Action ====================

/**
 * 异步获取门店列表的thunk action
 * 支持多服务类型和多种数据格式处理
 * @param {Object} options - 请求选项
 * @param {string} options.serviceType - 服务类型，默认为'default'
 * @param {string} options.dataType - 数据类型，控制格式化逻辑，默认为'default'
 * @param {Object} options.query - 查询参数对象
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchStoreList = createAsyncThunk(
  'store/fetchStoreList',
  async ({ serviceType = 'default', dataType = 'default', query = {} }, { rejectWithValue }) => {
    try {
      const response = await switchPayload(serviceType, query)();
      return {
        data: response.data,
        serviceType,
        dataType,
        query // 添加query到返回的payload中
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取门店列表失败');
    }
  }
);

// ==================== Redux Slice ====================

/**
 * 门店列表Redux slice
 * 管理门店列表的状态和操作，支持多服务类型数据存储
 */
const storeSlice = createSlice({
  name: 'store',
  initialState: {
    fetching: false,        // 全局加载状态
    error: null,           // 全局错误信息
    query: {},             // 查询参数集合，按服务类型存储
    default: [],           // 默认门店数据
    pictureList: [],        // 专门存储pictureList和default处理后的结果
    // 预留其他服务类型数据存储
    // featured: [],        // 特色门店
    // nearby: []           // 附近门店
  },
  reducers: {
    /**
     * 重置门店状态
     * @param {Object} state - 当前状态
     * @param {Object} action - action对象，payload可包含serviceType指定重置的服务类型
     */
    resetStoreState: (state, action) => {
      const { serviceType } = action.payload || {};
      if (!serviceType) {
        // 重置所有状态
        Object.keys(state).forEach(key => {
          if (Array.isArray(state[key])) {
            state[key] = [];
          }
        });
        state.query = {};
      } else {
        // 重置指定服务类型的数据
        state[serviceType] = [];
        state.query[serviceType] = {};
      }
    },

    /**
     * 更新查询参数
     * @param {Object} state - 当前状态
     * @param {Object} action - action对象，payload包含serviceType和query
     */
    setQuery: (state, action) => {
      const { serviceType = 'default', query } = action.payload;
      state.query[serviceType] = { ...state.query[serviceType], ...query };
    },

    /**
     * 设置分页参数
     * @param {Object} state - 当前状态
     * @param {Object} action - action对象，payload包含page和pageSize
     */
    setPagination: (state, action) => {
      const { serviceType = 'default', page, pageSize } = action.payload;
      if (page !== undefined) state.query[serviceType].page = page;
      if (pageSize !== undefined) state.query[serviceType].pageSize = pageSize;
    },

    /**
     * 更新门店数据
     * @param {Object} state - 当前状态
     * @param {Object} action - action对象，payload包含serviceType和查询参数
     */
    paginateStoreData: (state, action) => {
      if (!state || !action.payload) return state;
      const { dataType,  serviceType = 'default', query = {} } = action.payload;

      // 获取当前服务类型的字段映射
      const fieldMap = keyConfig.find(kc => kc.key === serviceType) || defaultKey;
      // 确保state[serviceType]存在且是数组
      // 处理数据
      // data, fieldMap, dataType, query
      const processedData = processSingleDataType({
        data: state.data,
        fieldMap,
        dataType,
        query
      });

      // 更新数据
      state[`${serviceType}_swiper`] = processedData;
      state.query[`${serviceType}`] = query;

      return state;
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理请求开始状态
      .addCase(fetchStoreList.pending, (state, action) => {
        // 修复：添加安全检查，防止action.meta.arg为undefined
        const arg = action.meta.arg || {};
        const { serviceType = 'default', query = {} } = arg;
        state.fetching = true;
        state.error = null;
        state.query[serviceType] = query;
        state[serviceType] = [];
      })
      // 处理请求成功状态
      .addCase(fetchStoreList.fulfilled, (state, action) => {
        const { data, serviceType, dataType, query } = action.payload;
        state.fetching = false;
        state.error = null;
        state.data = data; // 记录原始数据

        // 先初始化所有相关的状态字段
        const dataTypes = dataType.split(',').map(type => type.trim());
        // 根据serviceType和dataType格式化数据，传入query参数
        const formattedData = formatData({data, serviceType, dataType, query});

        // 更新所有数据类型对应的状态字段
        dataTypes.forEach(type => {
          // console.log('formattedData dataTypes:', type);
          const key = `${serviceType}_${type}`;
          state[key] = formattedData[type] || [];
        });
      })
      // 处理请求失败状态
      .addCase(fetchStoreList.rejected, (state, action) => {
        // 修复：添加安全检查，防止action.meta.arg为undefined
        const arg = action.meta.arg || {};
        const { serviceType = 'default' } = arg;
        state.fetching = false;
        state.error = action.payload;
        state[serviceType] = [];
        // 确保即使出错也不阻塞页面其他内容
        console.error('fetchStoreList 请求失败:', action.payload);
      });
  }
});

// ==================== 导出Action Creators ====================

// 导出所有同步action creators
export const { resetStoreState, setQuery, setPagination, paginateStoreData } = storeSlice.actions;

// ==================== 选择器函数集合 ====================

/**
 * 门店数据选择器集合
 * 提供统一的数据访问接口，封装状态选择逻辑
 */
export const storeSelectors = {
  /**
   * 获取指定服务类型的门店数据
   * @param {string} serviceType - 服务类型，默认为'default'
   * @returns {Function} 返回选择器函数
   */
  selectStoreData: (serviceType = 'default') => (state) =>
    state.store[serviceType] || [],

  /**
   * 获取指定服务类型和数据类型的门店数据
   * @param {string} serviceType - 服务类型，默认为'default'
   * @param {string} dataType - 数据类型，如'swiper'、'storeOption'等
   * @returns {Function} 返回选择器函数
   */
  selectStoreDataByType: (serviceType = 'default', dataType) => (state) => {
    if (!dataType) {
      return state.store[serviceType] || [];
    }
    const key = `${serviceType}_${dataType}`;
    return state.store[key] || [];
  },

  /**
   * 获取指定服务类型的查询参数
   * @param {string} serviceType - 服务类型，默认为'default'
   * @returns {Function} 返回选择器函数
   */
  selectStoreQuery: (serviceType = 'default') => (state) =>
    state.store.query[serviceType] || {},

  /**
   * 获取全局加载状态
   * @returns {Function} 返回选择器函数
   */
  selectStoreFetching: (state) => state.store.fetching,

  /**
   * 获取全局错误信息
   * @returns {Function} 返回选择器函数
   */
  selectStoreError: (state) => state.store.error,

  /**
   * 获取指定服务类型的分页信息
   * @param {string} serviceType - 服务类型，默认为'default'
   * @returns {Function} 返回选择器函数
   */
  selectStorePagination: (serviceType = 'default') => (state) => ({
    page: state.store.query[serviceType]?.page || 1,
    pageSize: state.store.query[serviceType]?.pageSize || 10
  })
};

// 为了向后兼容，导出常用的选择器函数
export const {
  selectStoreData,
  selectStoreDataByType,
  selectStoreQuery,
  selectStoreFetching,
  selectStoreError,
  selectStorePagination
} = storeSelectors;

// ==================== 导出Reducer ====================

export default storeSlice.reducer;